import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import _ from 'underscore';
import tinycolor from 'tinycolor2';
import { Close, GetApp, OpenInNew, Share } from '@material-ui/icons';
import { isSafari } from 'react-device-detect';

import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { usePlayer } from '../../../contexts/PlayerContext';
import { usePresenter } from '../../../contexts/PresenterContext';
import { PresenterHelper } from '../../Presenter/presenter-helper';
import { getEmbedShareSettings, getPreviewLink, joinClassNames } from '../../../util/util';
import ControlButton from '../ControlButton';

import {
	TopControlsAccountTitle,
	TopControlsContainer,
	TopControlsLeft,
	TopControlsLeftFirstRow,
	TopControlsMediaTitle,
	TopControlsPlaybackTitle,
	TopControlsPlaybackTitleWrapper,
	TopControlsProps,
	TopControlsRight,
	TopControlsShadow,
} from './TopControls.styled';
import './TopControls.scss';

const TopControls: React.FC<TopControlsProps> = ({ settings, onMouseEnter, onMouseLeave, children, isMobile, className, showTopControls }) => {
	const { t } = useTranslation();

	const { isPlaying, setPlayerCurrentPosition, setIsPlaying } = usePlayer();
	const { presentationPublishedSlides } = usePresenter();
	const { mediaName, videoDownloadUrl, playlistId, mediaType, accountId, embedShareSettings: embedShareSettingsMessage, isMediaLive, mediasData, currentMediaId, configParams, sceneConfig, scenario, setOpenSharingModal, setSceneConfig } = useConfiguration();

	const [overShowingTime, setOverShowingTime] = useState<boolean>(false);

	const isShowingTitle = settings?.topControls?.showTitleDuringPlayback
		? settings?.topControls?.showTitleDuringPlayback
		: !overShowingTime
			? true
			: !isPlaying;
	const isShowingAccName = settings?.topControls?.showAccountNameDuringPlayback
		? settings?.topControls?.showAccountNameDuringPlayback
		: !overShowingTime
			? true
			: !isPlaying;

	const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });
	const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
	const accountName = useMemo(() => {
		if (!settings.topControls?.accountName) {
			return '';
		}
		if (typeof settings.topControls?.accountName === 'string') {
			return settings.topControls?.accountName;
		}

		const currentLanguage = i18next.languages[0] === 'no' ? 'nb' : i18next.languages[0] === 'dk' ? 'da' : i18next.languages[0];
		return settings.topControls?.accountName[currentLanguage] ?? settings.topControls?.accountName['all'];
	}, [settings.topControls?.accountName]);

	const getLink = (isPlayerLink = false) => {
		const params = {
			mediaDetails: {
				id: mediaType === 'scenario' ? configParams.scenarioId : configParams.mediaId,
				title: mediaName || currentMedia?.mediaName,
			},
			configs: { id: configParams.configId, ...settings },
			paramConfigs: configParams,
			accountId: accountId,
			type: mediaType,
			time: undefined,
			embedShareSettings: isPlayerLink ? undefined : embedShareSettings,
		};
		return getPreviewLink({ ...params });
	};

	const handleOpenInNewWindow = () => {
		const windowUrl = configParams.container ? getLink(true) : window.location.href;
		window.open(windowUrl, 'newwindow', 'width=606,height=360');
	};

	const handleShare = () => {
		if (isMobile) {
			navigator.share && navigator.share({ url: getLink() });
			return;
		}
		setOpenSharingModal(true);
	};

	const handleDownloadVideo = () => {
		window.open(rewriteUrl((window as any).GoBrain.Utils.setQueryStringParameter(videoDownloadUrl, 'download', true)));
	};

	const rewriteUrl = (url: string) => {
		var topDomain = -1 < url.indexOf('.dna.qbrick.com') ? '.dna.qbrick.com' : -1 < url.indexOf('.dna.local') ? '.dna.local' : null;
		if (topDomain) {
			var subDomains = url.substring(2 + url.indexOf('//'), url.indexOf(topDomain));
			url = url.replace(subDomains, subDomains.replace(/\./g, '-'));
		}

		return 'file:' !== window.location.protocol ? url.replace(/^http[s]?:/, window.location.protocol) : url;
	};

	const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);

	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
	const enabledTopControlByPreset = settings.topControls.enabled;
    const hasPresentation = PresenterHelper.hasPresentationLayout({ publishedSlides: presentationPublishedSlides, media: currentMedia });

	const isInfoExcludedOnThisHost = (option: any, domainList: any) => {
		let isInfoHidden = false;

		const titleDomainList = (domainList ?? '').split(',');

		if (option && titleDomainList.length !== 0) {
			const url = embedShareSettings?.origin ?? '';
			isInfoHidden = titleDomainList.some((domain: any) => domain && url.includes(domain.trim()));
		}

		return isInfoHidden;
	};

	const mediaTitleHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings.topControls?.showExcludingTitleOnDomains,
		settings.topControls?.excludingTitleOnDomains
	);

	const companyNameHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings.topControls?.showExcludingAccountNameOnDomains,
		settings.topControls?.excludingAccountNameOnDomains
	);

	const solidColor = settings?.topControls?.solidBackgroundTopColor ?? '#00000';
	const solidColorStyle = settings.topControls.showSolidBackgroundTopControl
		? {
			backgroundColor: tinycolor({
				...tinycolor(solidColor).toRgb(),
				a: Number(settings.topControls.solidBackgroundTopControlOpacity ?? 100) / 100,
			}).toRgbString(),
		}
		: undefined;

	const { sharing, downloadMedia, openInNewWindow } = configParams ?? {};
	const showShareButton = !sharing ? (enabledTopControlByPreset && settings.topControls?.showSocialSharing) : sharing === "true";
	const showDownloadButton = !downloadMedia ? (enabledTopControlByPreset && settings.topControls?.showDownloadButton) : downloadMedia === "true";
	const showOpenInNewWindow = !openInNewWindow ? (enabledTopControlByPreset && settings.topControls?.showOpenInNewWindow) : openInNewWindow === "true";

	useEffect(() => {
		const clearTimeId = setTimeout(() => {
			setOverShowingTime(true);
		}, 3000);

		return () => {
			clearTimeout(clearTimeId);
		};
	}, []);

	return (
		<TopControlsContainer className={joinClassNames(configParams.inline === 'off' && !configParams.container ? 'top-control--not-inline' : '', className)}>
			{showTopControls && playlistId === '' && !hasPresentation  && (
				<TopControlsLeft className="topControlsLeft">
					<TopControlsLeftFirstRow>
						{enabledTopControlByPreset &&
							settings.topControls?.showLogotype &&
							settings.topControls?.logotypeUrl &&
							!companyNameHiddenOnThisHost && (
								<img
									style={{
										opacity: `${isShowingAccName ? 1 : 0}`,
										transition: 'all 0.5s',
									}}
									aria-hidden={true}
									className="topControlsLeft__logo"
									src={settings.topControls?.logotypeUrl}
								/>
							)}
						<TopControlsPlaybackTitleWrapper>
							{enabledTopControlByPreset && settings.topControls?.showTitle && !mediaTitleHiddenOnThisHost && (
								<TopControlsPlaybackTitle
									className="topControlbar__control--title"
									aria-hidden={!isShowingTitle}
									aria-label={`${t('ARIA_LABEL_MEDIA_TITLE')}${mediaName || currentMedia?.mediaName}`}
									style={{
										...solidColorStyle,
										width: 'fit-content',
										borderRadius: '4px',
										padding: '4px',
										opacity: `${isShowingTitle ? 1 : 0}`,
										transition: 'all 0.5s',
									}}
									role="heading"
									aria-level={2}
								>
									<TopControlsMediaTitle aria-hidden="true" isMobile={isMobile}>
										{mediaName || currentMedia?.mediaName}
									</TopControlsMediaTitle>
								</TopControlsPlaybackTitle>
							)}
							{enabledTopControlByPreset && settings.topControls?.showAccountName && accountName && !companyNameHiddenOnThisHost && (
								<TopControlsPlaybackTitle
									className="topControlbar__control--accountName"
									aria-label={`${t('ARIA_LABEL_PUBLISHER')}${accountName}`}
									aria-hidden={!isShowingAccName}
									style={{
										...solidColorStyle,
										width: 'fit-content',
										borderRadius: '4px',
										padding: '4px',
										opacity: `${isShowingAccName ? 1 : 0}`,
										transition: 'all 0.5s',
									}}
									role="heading"
									aria-level={2}
								>
									<TopControlsAccountTitle aria-hidden="true" isMobile={isMobile}>
										{accountName}
									</TopControlsAccountTitle>
								</TopControlsPlaybackTitle>
							)}
						</TopControlsPlaybackTitleWrapper>
					</TopControlsLeftFirstRow>
				</TopControlsLeft>
			)}
			{showTopControls && children}
			<TopControlsRight className="topControlsRight" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
				{showTopControls && !isMediaLive && showDownloadButton && videoDownloadUrl && (
					<ControlButton
						icon={<GetApp htmlColor="currentColor" className={isSafari ? 'safari-fix' : ''} />}
						tabIndex={0}
						ariaLabel={t('DOWNLOAD')}
						tooltip={t('DOWNLOAD_TOOLTIP')}
						style={solidColorStyle}
						hoverStyle={hoverStyle}
						onClick={handleDownloadVideo}
						topControl
					/>
				)}

				{showTopControls && showShareButton && (
					<ControlButton
						className='qplayer_share_btn'
						icon={<Share htmlColor="currentColor" className={isSafari ? 'safari-fix' : ''} />}
						tabIndex={0}
						ariaLabel={t('SHARE')}
						tooltip={t('SHARE_TOOLTIP')}
						style={solidColorStyle}
						hoverStyle={hoverStyle}
						onClick={handleShare}
						topControl
					/>
				)}

				{showTopControls && showOpenInNewWindow && (
					<ControlButton
						icon={<OpenInNew htmlColor="currentColor" className={isSafari ? 'safari-fix' : ''} />}
						tabIndex={0}
						aria-label={t('OPEN_IN_NEW_WINDOW')}
						tooltip={t('OPEN_IN_NEW_WINDOW')}
						style={solidColorStyle}
						hoverStyle={hoverStyle}
						onClick={handleOpenInNewWindow}
						topControl
					/>
				)}

				{configParams.inline === "off" && !!configParams.container && <ControlButton
					tabIndex={0}
					ariaLabel={t('LABEL_CLOSE')}
					tooltip={t('LABEL_CLOSE')}
					hoverStyle={hoverStyle}
					onClick={() => {
						if (configParams.container) {
							configParams.container.setAttribute('data-qplayer-modal-open', 'false');
							const playerId = `player-${playlistId || currentMediaId}-${configParams.containerKey}`;
							playerId && (window as any).GoBrain.widgets(playerId)?.pause?.();
							playerId && (window as any).GoBrain.widgets(playerId)?.position?.(0);
							setPlayerCurrentPosition(0)
							setIsPlaying(undefined);
							if (sceneConfig.currentScene === scenario?.defaults?.sceneId || !scenario?.defaults?.sceneId) {
								return;
							}
							setSceneConfig({ currentScene: scenario?.defaults?.sceneId });
						}
					}}
					icon={<Close htmlColor="currentColor" className={isSafari ? 'safari-fix' : ''} />}
					className='qplayer-close-modal'
					style={configParams.useLoader === "true" ? { visibility: "hidden" } : {}}
					topControl
				/>}

				{configParams.topRightSpace && <ControlButton style={{ cursor: "default" }} />}
			</TopControlsRight>
			{showTopControls && enabledTopControlByPreset && settings.topControls?.showHoverShadow && <TopControlsShadow aria-hidden="true" />}
		</TopControlsContainer>
	);
};

export default TopControls;
